import { gql } from '@plato/app-core/gql';
import { useQuery } from '@plato/app-core/hooks/use-query';
import { filterViewTypenames, SavedFilterViews } from '@plato/app-shared/components/saved-filter-views';
import { omit } from 'lodash-es';
import type { ReactElement } from 'react';
import { mapToTargetUnit } from '../../../server/utils/explorer-utils.ts';
import {
    clearedExplorerSearches,
    useExplorerView,
    useInitialExplorerState,
    type ExplorerState,
} from '../hooks/use-explorer-query-params.ts';

const explorerSavedFilterViewsQuery = gql(/* GraphQL */ `
    query ExplorerSavedFilterViews($viewKey: String!) {
        filterViews(viewKeys: [$viewKey]) {
            ...SavedFilterViews_FilterView
        }
    }
`);

const explorerSavedViewKey = 'sales-report.v1';

const explorerUnsavedKeys = [
    'sorting',
    'searchString',
    'searchStringCustomer',
    'searchStringClerk',
    'searchStringIndustry',
    'searchStringProduct',
    'searchStringProductGroup',
    'searchStringStorageLocation',
    'searchStringSupplier',
    'searchStringSalesRep',
    'searchStringNormalisedCategory',
] as const satisfies readonly (keyof ExplorerState)[];

type ExplorerSavedFilters = Omit<ExplorerState, (typeof explorerUnsavedKeys)[number]>;

type ExplorerSavedFiltersSource = Omit<ExplorerSavedFilters, 'customDateRange'> &
    Partial<Pick<ExplorerSavedFilters, 'customDateRange'>> &
    Partial<Pick<ExplorerState, (typeof explorerUnsavedKeys)[number]>>;

function pickExplorerSavedFilters(state: ExplorerSavedFiltersSource): ExplorerSavedFilters {
    return { ...omit(state, explorerUnsavedKeys), customDateRange: state.customDateRange };
}

function applyExplorerSavedFilters(
    state: Pick<ExplorerState, 'tab' | 'sorting'>,
    defaultFilters: ExplorerSavedFilters,
    filters: Partial<ExplorerSavedFilters>,
): Partial<ExplorerState> {
    const next: ExplorerSavedFilters = { ...defaultFilters, ...omit(filters, explorerUnsavedKeys) };

    return {
        ...(next.tab === state.tab ? {} : clearedExplorerSearches),
        ...next,
        sorting: {
            ...state.sorting,
            field: mapToTargetUnit[next.targetUnit][state.sorting.field],
        },
    };
}

export function ExplorerSavedFilterViews(): ReactElement {
    const { state, setState } = useExplorerView();
    const defaultFilters = { ...pickExplorerSavedFilters(useInitialExplorerState()), tab: state.tab };
    const [{ data }] = useQuery({
        query: explorerSavedFilterViewsQuery,
        variables: { viewKey: explorerSavedViewKey },
        context: filterViewTypenames,
    });

    return (
        <SavedFilterViews<ExplorerSavedFilters>
            views={data?.filterViews}
            viewKey={explorerSavedViewKey}
            appliedFilters={pickExplorerSavedFilters(state)}
            defaultFilters={defaultFilters}
            onApplyFilters={(filters) => {
                setState((prevState) => ({
                    ...prevState,
                    ...applyExplorerSavedFilters(state, defaultFilters, filters),
                }));
            }}
        />
    );
}
